import * as THREE from 'three';
import { getMuzzleFlashPool } from './pool';
import { EFFECT_LAYER } from './enhanced-effects';
import { addEffect } from './effect-loop';
import { GRAVITY } from '../core/config';

// ---------------------------------------------------------------------------
// Visual effects — muzzle flash, bullet impacts, tracers, hit markers,
// blood, screen shake
// ---------------------------------------------------------------------------

// ---- Muzzle Flash ---------------------------------------------------------

/** Flash a pooled orange point light + sprite at the given world position. */
export function spawnMuzzleFlash(position: THREE.Vector3): void {
  const pool = getMuzzleFlashPool();
  const light = pool.acquire();
  if (!light) return;

  light.position.copy(position);
  light.intensity = 3.0;
  light.visible = true;

  // Quick decay over 60ms — managed by central effect loop
  const start = performance.now();
  addEffect({
    update(now) {
      const t = (now - start) / 60;
      if (t >= 1) {
        light.visible = false;
        pool.release(light);
        return false;
      }
      light.intensity = 3.0 * (1 - t);
      return true;
    },
  });
}

// ---- Hit Marker -----------------------------------------------------------

let hitMarkerTimer: ReturnType<typeof setTimeout> | null = null;

export function showHitMarker(): void {
  const el = document.getElementById('hit-marker');
  if (!el) return;

  el.style.display = 'block';
  if (hitMarkerTimer !== null) clearTimeout(hitMarkerTimer);
  hitMarkerTimer = setTimeout(() => {
    el.style.display = 'none';
    hitMarkerTimer = null;
  }, 200);
}

// ---- Tracer Effect --------------------------------------------------------

/** Tracer color presets. */
export type TracerColor = 'normal' | 'incendiary';

// Shared tracer materials — yellow for normal rounds, red for incendiary/molotov.
// `let` so disposeTracerPool() can null-after-dispose and getTracerPool() can
// re-create on subsequent Game() construction (F-RN-002).
const tracerMaterials: Record<TracerColor, THREE.LineBasicMaterial> = {
  normal: new THREE.LineBasicMaterial({
    color: 0xffdd44,
    transparent: true,
    opacity: 0.8,
  }),
  incendiary: new THREE.LineBasicMaterial({
    color: 0xff3322,
    transparent: true,
    opacity: 0.8,
  }),
};

/** Tracer visual travel speed in m/s. */
export const TRACER_SPEED = 300;

/** Tracer visible lifetime in ms. */
export const TRACER_LIFETIME_MS = 100;

// Shared scratch vectors for tracer update — safe because the effect loop
// runs callbacks synchronously on one thread.
const _tracerHeadScratch = new THREE.Vector3();
const _tracerTailScratch = new THREE.Vector3();

// Pool of pre-allocated tracer Line objects.
// F-RN-016: per-slot persistent scratch (origin + direction) so spawnTracer
// no longer allocates Vector3s with `to.clone().sub(from)` every fire (auto +
// 600 RPM previously burned 30 transient Vector3s per second).
const TRACER_POOL_SIZE = 50;
type TracerSlot = {
  line: THREE.Line;
  geometry: THREE.BufferGeometry;
  active: boolean;
  parent: THREE.Object3D | null;
  startTime: number;
  origin: THREE.Vector3;
  direction: THREE.Vector3;
  fullDist: number;
  tracerLen: number;
};

// F-RN-002: lazy-init the pool so disposeTracerPool() + a subsequent Game()
// rebuild gets a fresh set of GPU buffers. The previous module-level loop ran
// once at import; after disposeTracerPool() the same array still pointed at
// disposed BufferGeometry objects.
let tracerPool: TracerSlot[] = [];
let _tracerMaterialsDisposed = false;
function getTracerPool(): TracerSlot[] {
  if (tracerPool.length > 0) return tracerPool;
  if (_tracerMaterialsDisposed) {
    tracerMaterials.normal = new THREE.LineBasicMaterial({ color: 0xffdd44, transparent: true, opacity: 0.8 });
    tracerMaterials.incendiary = new THREE.LineBasicMaterial({ color: 0xff3322, transparent: true, opacity: 0.8 });
    _tracerMaterialsDisposed = false;
  }
  for (let i = 0; i < TRACER_POOL_SIZE; i++) {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(6); // 2 points × 3 components
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const line = new THREE.Line(geometry, tracerMaterials.normal.clone());
    line.layers.set(EFFECT_LAYER);
    line.visible = false;
    tracerPool.push({
      line,
      geometry,
      active: false,
      parent: null,
      startTime: 0,
      origin: new THREE.Vector3(),
      direction: new THREE.Vector3(),
      fullDist: 0,
      tracerLen: 0,
    });
  }
  return tracerPool;
}

/**
 * Draw a tracer line from `from` to `to`, fading over TRACER_LIFETIME_MS.
 * Supports color variation: 'normal' (yellow) for standard rounds,
 * 'incendiary' (red) for fire-based weapons.
 * Works for both local and remote player shots.
 *
 * @param from — world-space origin of the tracer
 * @param to — world-space hit/end point
 * @param parent — scene or object to attach the tracer to
 * @param color — tracer color preset (default: 'normal')
 */
export function spawnTracer(
  from: THREE.Vector3,
  to: THREE.Vector3,
  parent: THREE.Object3D,
  color: TracerColor = 'normal',
): void {
  // F-RN-002: getTracerPool() lazy-builds pool slots on first use after
  // disposeTracerPool(), so post-restart spawnTracer() never writes into
  // disposed BufferGeometry objects.
  const pool = getTracerPool();

  // Find an inactive slot, or recycle the oldest active tracer
  let slot = pool.find(s => !s.active);
  if (!slot) {
    if (pool.length === 0) return;
    // Recycle the oldest (lowest startTime) active tracer
    let oldest = pool[0];
    for (let i = 1; i < pool.length; i++) {
      if (pool[i].startTime < oldest.startTime) oldest = pool[i];
    }
    // Remove from old parent
    if (oldest.parent) {
      oldest.parent.remove(oldest.line);
      oldest.parent = null;
    }
    oldest.line.visible = false;
    oldest.active = false;
    slot = oldest;
  }

  // F-RN-016: write spawn pose into per-slot scratch instead of cloning Vector3s.
  slot.origin.copy(from);
  slot.direction.copy(to).sub(from);
  const dist = slot.direction.length();
  slot.fullDist = dist;
  slot.tracerLen = Math.min(dist, 1.5);
  if (dist > 0) slot.direction.divideScalar(dist); // normalize without clone

  // Tracer end head position (reuse scratch — only used inline below).
  _tracerHeadScratch.copy(from).addScaledVector(slot.direction, slot.tracerLen);

  // Set positions via attribute
  const posAttr = slot.geometry.attributes.position as THREE.BufferAttribute;
  posAttr.setXYZ(0, from.x, from.y, from.z);
  posAttr.setXYZ(1, _tracerHeadScratch.x, _tracerHeadScratch.y, _tracerHeadScratch.z);
  posAttr.needsUpdate = true;
  slot.geometry.computeBoundingSphere();

  // Apply color
  const lineMat = slot.line.material as THREE.LineBasicMaterial;
  const refMat = tracerMaterials[color];
  lineMat.color.copy(refMat.color);
  lineMat.opacity = 0.8;
  slot.line.visible = true;
  slot.active = true;
  slot.parent = parent;
  slot.startTime = performance.now();
  parent.add(slot.line);

  const start = slot.startTime;
  const DURATION = TRACER_LIFETIME_MS;
  const entry = slot;

  // Animate tracer: travel toward target at TRACER_SPEED, then fade.
  // F-RN-016: read scratch fields off `entry` so the closure captures no
  // per-spawn Vector3 state — every tracer reuses its slot's persistent
  // origin/direction.
  addEffect({
    update(now) {
      const elapsed = now - start;
      const t = Math.min(elapsed / DURATION, 1);

      // Move tracer along the shot direction (visual travel at TRACER_SPEED)
      const traveled = Math.min((elapsed / 1000) * TRACER_SPEED, entry.fullDist);
      const headPos = _tracerHeadScratch.copy(entry.origin).addScaledVector(entry.direction, traveled);
      const tailPos = _tracerTailScratch.copy(entry.origin).addScaledVector(entry.direction, Math.max(0, traveled - entry.tracerLen));

      const attr = entry.geometry.attributes.position as THREE.BufferAttribute;
      attr.setXYZ(0, tailPos.x, tailPos.y, tailPos.z);
      attr.setXYZ(1, headPos.x, headPos.y, headPos.z);
      attr.needsUpdate = true;

      (entry.line.material as THREE.LineBasicMaterial).opacity = 0.8 * (1 - t);

      if (t >= 1) {
        if (entry.parent) {
          entry.parent.remove(entry.line);
          entry.parent = null;
        }
        entry.line.visible = false;
        entry.active = false;
        return false;
      }
      return true;
    },
  });
}

/** Dispose all tracer pool geometries and materials (call on cleanup/shutdown).
 *  F-RN-002: clears the pool array so getTracerPool() rebuilds on next use. */
export function disposeTracerPool(): void {
  for (const slot of tracerPool) {
    slot.geometry.dispose();
    (slot.line.material as THREE.LineBasicMaterial).dispose();
    if (slot.parent) slot.parent.remove(slot.line);
  }
  tracerPool = [];
  tracerMaterials.normal.dispose();
  tracerMaterials.incendiary.dispose();
  _tracerMaterialsDisposed = true;
}

// ---- Blood Effect ---------------------------------------------------------

// F-RN-002: lazy blood resources. The previous module-level allocation ran
// once at first import; after disposeBloodPool() the same `const` references
// pointed at disposed objects, so the next Game() instance burst spawning
// blood would write into freed GPU buffers.
const BLOOD_MATERIAL_POOL_SIZE = 10;
let bloodGeometry: THREE.SphereGeometry | null = null;
let bloodMaterialPool: THREE.MeshBasicMaterial[] = [];

function getBloodResources(): {
  geometry: THREE.SphereGeometry;
  pool: THREE.MeshBasicMaterial[];
} {
  if (!bloodGeometry) {
    bloodGeometry = new THREE.SphereGeometry(0.04, 4, 4);
  }
  if (bloodMaterialPool.length === 0) {
    for (let i = 0; i < BLOOD_MATERIAL_POOL_SIZE; i++) {
      // Vary red shade slightly: 0xaa0000 to 0xdd0000
      const red = 0xAA + Math.floor((i / (BLOOD_MATERIAL_POOL_SIZE - 1)) * 0x33);
      const shade = red << 16;
      bloodMaterialPool.push(new THREE.MeshBasicMaterial({ color: shade, transparent: true, opacity: 1 }));
    }
  }
  return { geometry: bloodGeometry, pool: bloodMaterialPool };
}

/**
 * Burst 4-7 small red spheres at `position` that fall with gravity
 * and fade over 600ms.
 */
export function spawnBloodEffect(
  position: THREE.Vector3,
  parent: THREE.Object3D,
): void {
  const COUNT = 4 + Math.floor(Math.random() * 4);
  const BLOOD_GRAVITY = GRAVITY * 0.6;
  const LIFETIME = 600;

  const particles: Array<{
    mesh: THREE.Mesh;
    velocity: THREE.Vector3;
  }> = [];

  // F-RN-002: getBloodResources() rebuilds geometry + pool after a previous
  // disposeBloodPool() so a fresh Game() instance never writes disposed GPU.
  const { geometry: bloodGeo, pool: bloodPool } = getBloodResources();

  for (let i = 0; i < COUNT; i++) {
    // Pick a pooled material (round-robin with random offset)
    const pooledMat = bloodPool[Math.floor(Math.random() * BLOOD_MATERIAL_POOL_SIZE)];
    const m = new THREE.Mesh(bloodGeo, pooledMat);
    m.position.copy(position);
    m.userData.isEffect = true;
    m.layers.set(EFFECT_LAYER);
    parent.add(m);

    particles.push({
      mesh: m,
      velocity: new THREE.Vector3(
        (Math.random() - 0.5) * 4,
        Math.random() * 3 + 1,
        (Math.random() - 0.5) * 4,
      ),
    });
  }

  const start = performance.now();
  let lastFrame = start;

  // Blood particle animation — managed by central effect loop
  addEffect({
    update(now) {
      const elapsed = now - start;
      const dt = Math.min((now - lastFrame) / 1000, 0.05);
      lastFrame = now;

      if (elapsed >= LIFETIME) {
        for (const p of particles) {
          parent.remove(p.mesh);
        }
        return false;
      }

      const fadeT = elapsed / LIFETIME;
      for (const p of particles) {
        p.velocity.y += BLOOD_GRAVITY * dt;
        p.mesh.position.addScaledVector(p.velocity, dt);

        // Fade via scale (shared material cannot have per-instance opacity)
        const s = 1 - fadeT;
        p.mesh.scale.set(s, s, s);
      }

      return true;
    },
  });
}

/** Dispose pooled blood resources (call on cleanup/shutdown).
 *  F-RN-002: nulls module-level refs so getBloodResources() rebuilds on
 *  next spawn after a Game.dispose() + new Game() cycle. */
export function disposeBloodPool(): void {
  bloodGeometry?.dispose();
  bloodGeometry = null;
  for (const mat of bloodMaterialPool) mat.dispose();
  bloodMaterialPool = [];
}

// ---- Screen Shake ---------------------------------------------------------

/** Active shake state. */
let shakeIntensity = 0;
let shakeDecay = 0;

/**
 * Trigger a camera shake. Call once per event (getting hit, firing heavy weapon).
 * @param intensity — max pixel offset (0.01 = subtle, 0.05 = heavy)
 * @param duration — seconds to decay to zero
 */
export function triggerScreenShake(intensity: number, duration: number): void {
  // Only update decay when incoming intensity exceeds current, otherwise
  // a weaker shake would reset the decay rate of a stronger ongoing shake.
  if (intensity > shakeIntensity) {
    shakeDecay = intensity / duration;
  }
  shakeIntensity = Math.max(shakeIntensity, intensity);
}

/**
 * Apply shake offset to a camera. Call every frame.
 * Returns the offset applied so it can be undone next frame.
 */
export function applyScreenShake(
  camera: THREE.Camera,
  dt: number,
): { x: number; y: number } {
  if (shakeIntensity <= 0) return { x: 0, y: 0 };

  const offX = (Math.random() - 0.5) * 2 * shakeIntensity;
  const offY = (Math.random() - 0.5) * 2 * shakeIntensity;

  camera.position.x += offX;
  camera.position.y += offY;

  shakeIntensity = Math.max(0, shakeIntensity - shakeDecay * dt);

  return { x: offX, y: offY };
}

/**
 * Remove the shake offset applied this frame.
 */
export function undoScreenShake(
  camera: THREE.Camera,
  offset: { x: number; y: number },
): void {
  camera.position.x -= offset.x;
  camera.position.y -= offset.y;
}
